import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import type { Vault } from 'blockchain/connector.ts';
import dayjs from 'common/dayjs.ts';
import { chunk } from 'es-toolkit';
import type { Alert } from 'types/Alert.ts';
import type { DocumentBC } from 'types/Document.ts';
import type { LEG_COLOR, LEG_STATUS, Leg } from 'types/Leg.ts';
import type { Point } from 'types/misc.ts';
import type { OPERATION, PermissionParsed } from 'types/Permission.ts';
import type { SHIPMENT_COLOR, SHIPMENT_STATUS, Shipment } from 'types/Shipment.ts';
import { BaseError, errorCodes, errorMessages } from '#common/errors/index.ts';

export type request_input = any; // [any, any, any?];

export const haveSameMembers = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};

export const hasExtras = (a: any[], b: any[]): boolean => b.some((item) => !a.includes(item));

export const unique = <T>(value: T, index: number, array: T[]): boolean => array.indexOf(value) === index;

export const uniqueBy =
  <T>(property: keyof T) =>
  (value: T, index: number, array: T[]): boolean =>
    array.findIndex((obj) => obj[property] === value[property]) === index;

export const GET = async ([url, params, transformRequest, transformResponse]: request_input) => {
  if (typeof params === 'string') params = {}; // the second param in the cache key may not be actual params, but only a token (eg: useMe)
  const { data } = await axios.get(url, { params: transformRequest ? transformRequest(params) : params });
  const result = transformResponse ? transformResponse(data) : data;
  return result;
};

export const POST = async ([url, params, transformRequest, transformResponse]: request_input) => {
  const { data } = await axios.post(url, transformRequest ? transformRequest(params) : params);
  const result = transformResponse ? transformResponse(data) : data;
  return result;
};

export const DELETE = async ([url, params, transform]: request_input) => {
  const { data } = await axios.delete(url, {
    params,
  });
  const result = transform ? transform(data) : data;
  return result;
};

export const MPOST = async (url, { arg }, config?: AxiosRequestConfig) => {
  let params;

  if (Array.isArray(arg)) {
    const [values, transform] = arg;
    params = transform(values);
  } else {
    params = arg;
  }

  const { data } = await axios.post(url, params, config);
  return data;
};

export const MultipartMPOST = async (url, { arg }, config?: AxiosRequestConfig) => {
  return MPOST(url, { arg }, { headers: { 'Content-Type': 'multipart/form-data' }, ...config });
};

export const MPUT = async (url, { arg }) => {
  let params;

  if (Array.isArray(arg)) {
    const [values, transform] = arg;
    params = transform(values);
  } else {
    params = arg;
  }

  const { data } = await axios.put(url, params);
  return data;
};

// this helper removes null and '' values from an object, and it's useful when dealing with inputs which typically can only hold strings
export const emptyToUndefined = (obj: Record<string, any>) => {
  const result = { ...obj };
  for (const [key, value] of Object.entries(result)) {
    if (value === '' || value === null) delete result[key];
  }
  return result;
};

// where-keys are `column$operator` (eg. `status$in`), with `eq` as the implicit default (`status` === `status$eq`).
// Repository.applyWhereWithOperators is what turns these into SQL; this helper exists so every caller that needs
// to reason about the operator (eg. to skip empty sets) splits the key exactly the same way.
export const parseWhereKey = (key: string): { column: string; operator: string } => {
  const [column, operator = 'eq'] = key.split('$');
  return { column, operator };
};

// this helper follows fromQuerystring/toQuerystring helpers dealing with undefined, datetimes and booleans
export const parseFormValues = (obj: Record<string, any>) => {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === '' || value === null) continue;
    // an empty $in/$nin means the filter was left untouched (eg. status$in with no status selected), so omit
    // the key: the API applies an empty set as "match nothing". Only those two operators are omitted — other
    // empty arrays are real payload (eg. user_ids on /device/last-positions, which requires the field).
    else if (Array.isArray(value) && value.length === 0 && ['in', 'nin'].includes(parseWhereKey(key).operator)) continue;
    else if (stringIsDate(value)) {
      // date-only $to values (YYYY-MM-DD) expand to end of UTC day so a single-day range returns results
      if (parseWhereKey(key).operator === 'to' && /^\d{4}-\d{2}-\d{2}$/.test(value)) result[key] = dayjs.utc(value).endOf('day').toISOString();
      else result[key] = new Date(value as string).toISOString();
    } else if (stringIsBoolean(value)) result[key] = stringToBoolean(value);
    else result[key] = value;
  }
  return result;
};

export const valueToDate = (value): string => {
  if ([undefined, null].includes(value)) return 'N/A';
  const date = new Date(String(value)).toLocaleDateString(undefined, { dateStyle: 'short' });
  return date;
};

export const valueToDatetime = (value): string => {
  if ([null, undefined].includes(value)) return 'N/A';
  const date = new Date(String(value)).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  return date;
};

export const valueToAmount = (value, currency = 'EUR'): string => {
  if ([null, undefined].includes(value)) return 'N/A';

  const amount = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));

  return amount;
};

export const valueToNumber = (value, suffix = '') => {
  if ([null, undefined].includes(value)) return 'N/A';

  let amount = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

  amount += suffix;

  return amount;
};

export const valueToYESNO = (value: any) => {
  if ([null, undefined].includes(value)) return 'N/A';
  const yesno = Boolean(value) === true ? 'YES' : 'NO';
  return yesno;
};

export const openGoogleMaps = (q: string) => (event) => {
  event.stopPropagation();
  window.open(`https://maps.google.com/?q=${encodeURIComponent(q)}`);
};

export const openURL = (url) => (event) => {
  event.stopPropagation();
  window.open(url);
};

// postcodes are free text with no normalization on write, so compare case- and whitespace-insensitively (matches the DB-side regexp_replace(lower(...), '\s', '', 'g'))
export const normalizePostcode = (postcode: string): string => postcode.replace(/\s+/g, '').toLowerCase();

type obj_from_qs = Record<string, string | string[] | boolean>;

export const stringIsBoolean = (value: any) => ['true', 'false'].includes(value);

export const stringToBoolean = (value: any): boolean => {
  if (value.toLowerCase() === 'true') return true;
  if (value.toLowerCase() === 'false') return false;
  throw new Error(`stringToBoolean: ${value} is not a boolean.`);
};

// new Date(1) and new Date("1") are valid, so we have to check for a string not being a number
export const stringIsDate = (value: any): boolean => typeof value === 'string' && value.includes('-') && !Number.isNaN(Date.parse(value));

export const stringToInputDate = (value: any): string => {
  if (!value) return '';
  try {
    const result = dayjs(value).format('YYYY-MM-DD'); // format iso string to local YYYY-MM-DD, so input will have the right value
    return result;
  } catch {
    throw new Error(`stringToInputDate: ${value} is not a valid ISO8601 string.`);
  }
};

export const stringToInputDatetime = (value: any): string => {
  if (!value) return '';
  try {
    const result = dayjs(value).format('YYYY-MM-DDTHH:mm'); // format iso string to local YYYY-MM-DDTHH:mm, so input will have the right value
    return result;
  } catch {
    throw new Error(`stringToInputDatetime: ${value} is not a valid ISO8601 string.`);
  }
};

type querystringParserOptions = {
  excludeKeys?: string[];
  arrayKeys?: string[];
};

export const fromQuerystring = (qs: URLSearchParams, options: querystringParserOptions = { excludeKeys: [], arrayKeys: [] }): obj_from_qs => {
  const result: obj_from_qs = Object.fromEntries(qs);

  for (const [key, value] of Object.entries(result)) {
    if (value === '') continue;
    if (options.excludeKeys?.includes(key)) {
      delete result[key];
      continue;
    }
    if (options.arrayKeys?.includes(key)) {
      result[key] = qs.getAll(key); // keep the array; skip scalar coercions below (e.g. postcodes like 00-001 must not be parsed as dates)
      continue;
    }
    if (stringIsBoolean(value)) result[key] = stringToBoolean(value); // convert possible boolean value automatically
    if (stringIsDate(value)) result[key] = stringToInputDate(value as any); // convert possible datetime value automatically
  }

  return result;
};

export const toQuerystring = (obj: obj_from_qs, options: querystringParserOptions = { excludeKeys: [], arrayKeys: [] }): string => {
  const urlSearchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(obj)) {
    if (['', null, undefined].includes(value as any)) continue;
    if (options.excludeKeys?.includes(key)) continue;
    if (options.arrayKeys?.includes(key)) {
      if (!Array.isArray(value)) throw new Error(`toQuerystring: ${key} is not an array.`);
      value.forEach((v) => urlSearchParams.append(key, String(v)));
    } else if (typeof value === 'boolean') urlSearchParams.append(key, String(value));
    else if (stringIsDate(value)) urlSearchParams.append(key, new Date(value as string).toISOString());
    else urlSearchParams.set(key, String(value));
  }

  const result = urlSearchParams.toString();
  return result;
};

export const truncate = (text: string, length: number, suffix = '...') => (text.length > length ? text.substring(0, length) + suffix : text);

export const isResponseError = (error: any) => error && axios.isAxiosError(error) && error.response?.status === 400;

export const getResponseError = (error: any, defaultMessage = '') => (isResponseError(error) ? error.response.data : defaultMessage);

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getRandomString = (length = 10) => Math.random().toString(20).slice(2, length).toUpperCase();

export const getFingerprint = (): string => {
  const current_fingerprint = window.localStorage.getItem('atlas:fingerprint');
  if (current_fingerprint) return current_fingerprint;

  const new_fingerprint = window.crypto.getRandomValues(new Uint32Array(1))[0].toString(16);
  window.localStorage.setItem('atlas:fingerprint', new_fingerprint);
  return new_fingerprint;
};

export const stringToPosition = (str: string): Point => {
  const [lon, lat] = str.split(',').map(Number);
  return [lon, lat];
};

export const computeDistance = (point1: Point, point2: Point): number => {
  const [lon1, lat1] = point1.map(Number);
  const [lon2, lat2] = point2.map(Number);
  const R = 6371e3;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const deltaLon = lon2 - lon1;
  const deltaLambda = (deltaLon * Math.PI) / 180;
  const d = Math.acos(Math.sin(p1) * Math.sin(p2) + Math.cos(p1) * Math.cos(p2) * Math.cos(deltaLambda)) * R;
  return d;
};

export const getETAStatusMessage = (shipment: Shipment): string => {
  const status = shipment.status;
  const activeLeg = getActiveLeg(shipment);
  const isToBeCollected = shouldBeCollected(activeLeg);
  const isToBeDelivered = shouldBeDelivered(activeLeg);
  if (isToBeCollected) {
    const isLate = dayjs().isAfter(dayjs(activeLeg.origin_expected_datetime));
    return isLate ? 'Ready to pick up' : `Pick up ${dayjs(activeLeg.origin_expected_datetime).fromNow()}`;
  }
  if (isToBeDelivered) {
    const isLate = dayjs().isAfter(dayjs(activeLeg.destination_expected_datetime));
    return isLate ? 'Ready to deliver' : `Deliver ${dayjs(activeLeg.destination_expected_datetime).fromNow()}`;
  }
  return status;
};

export const getStatusColor = (status: SHIPMENT_STATUS): SHIPMENT_COLOR => {
  const colors: Record<SHIPMENT_STATUS, SHIPMENT_COLOR> = {
    draft: 'grey',
    pending: 'blue',
    transiting: 'blue',
    delivered: 'green',
    cancelled: 'red',
  };
  return colors[status];
};

/**
 * Leg status colouring for the status `Tag`, the stepper and the map polylines: green only once
 * delivered, so an in-transit leg is never mistaken for a completed one.
 * Consumers pick the vocabulary they need: the `Tag` variant classes take the name as-is,
 * the stepper maps it through its own fill record, and the map uses LEG_COLOR_STYLE below.
 * Note `statusToFill` below still drives the map's numbered waypoint markers off a separate map.
 */
export const getLegStatusColor = (status: LEG_STATUS): LEG_COLOR => {
  const colors: Record<LEG_STATUS, LEG_COLOR> = {
    pending: 'grey',
    transiting: 'aqua',
    delivered: 'green',
    cancelled: 'red',
  };
  return colors[status];
};

/**
 * Raw CSS colours for consumers that cannot take a class, e.g. Leaflet polylines.
 * Hue and alpha are kept apart so the consumer sets both: baking the alpha into the hex would leave
 * whatever opacity the consumer already applies to multiply with it, and the colour would come out
 * darker than asked for. Consumers must pass the opacity, not just the hex.
 * Values are what the stepper renders for the same colour name: grey is --color-grey-light
 * (--color-grey-dark at 0.8), the rest are the solid --color-aqua/--color-green/--color-red.
 */
export const LEG_COLOR_STYLE: Record<LEG_COLOR, { hex: string; opacity: number }> = {
  grey: { hex: '#373a36', opacity: 0.8 },
  aqua: { hex: '#25d3ff', opacity: 1 },
  green: { hex: '#04994a', opacity: 1 },
  red: { hex: '#c22a23', opacity: 1 },
};

/**
 * This returns the color of the shipment based on the next leg's expected delivery time.
 * If the next leg is delayed, it will return red dark.
 * If the next leg's expected delivery time is in less than 30 minutes, it will return red (orange).
 * Otherwise, it will return an empty string since the shipment is on time.
 */
export const getShipmentDelayedColor = (shipment: Shipment) => {
  const currentLeg = getActiveLeg(shipment);
  if (!currentLeg) return '';

  if (currentLeg.delayed) return 'text-red-dark';

  const nextETA = getNextETA(shipment);
  if (nextETA && nextETA < 30) return 'text-yellow';
  return '';
};

/**
 * Returns the next ETA in minutes.
 * Next ETA is:
 * - the pickup expected datetime if the current leg was not yet picked up
 * - the delivery expected datetime if the current leg was not yet delivered
 * Negative values mean the shipment is already delayed.
 * Positive values mean the shipment is on time.
 */
export const getNextETA = (shipment: Shipment) => {
  const currentLeg = getActiveLeg(shipment);
  if (!currentLeg.next_eta) return undefined;

  const now = dayjs();
  const nextETATime = dayjs(currentLeg.next_eta);

  return nextETATime.diff(now, 'minutes');
};

export const getAlertsToRead = (alerts: Alert[]) => {
  return alerts.filter((alert) => !alert.archived_at);
};

// ref: https://github.com/colinhacks/zod/blob/3032e240a0c227692bb96eedf240ed493c53f54c/src/types.ts#L600
export const isValidEmail = (value: string) => {
  const emailRegex = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;
  return emailRegex.test(value);
};

export const isUUID = (value: string) => value.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);

// active leg is either the one having the next address to be reached by the driver, or the last leg if all legs are completed.
export const getActiveLeg = (shipment: Shipment) =>
  shipment.legs.find((leg: Leg) => leg.status === 'transiting') || shipment.legs.find((leg: Leg) => leg.status === 'pending') || shipment.legs[shipment.legs.length - 1];

export const isPastLegMissingPodDatetime = (shipment: Shipment) => {
  const dateTimes: (string | null)[] = [];
  let lastDateTimeIndex = -1;
  for (const leg of shipment.legs) {
    dateTimes.push(leg.origin_actual_datetime || null);
    dateTimes.push(leg.destination_actual_datetime || null);
    if (leg.origin_actual_datetime || leg.destination_actual_datetime) {
      lastDateTimeIndex = leg.destination_actual_datetime ? dateTimes.length - 1 : dateTimes.length - 2;
    }
  }

  if (lastDateTimeIndex === -1) return false;

  // Check if there is a null dateTime before the last non-null dateTime which means the POD datetime is missing
  return dateTimes.slice(0, lastDateTimeIndex).some((dateTime) => dateTime === null);
};

export const isShipmentLate = (shipment: Shipment) => {
  // Shipment is late if any leg is delayed (even after delivered)
  for (const leg of shipment.legs) {
    const now = dayjs();
    if (!leg.origin_actual_datetime && now.isAfter(leg.origin_expected_datetime)) return true;
    if (!leg.destination_actual_datetime && now.isAfter(leg.destination_expected_datetime)) return true;
  }
  return false;
};

export const getNextAddress = (shipment: Shipment) => {
  // Returns the next address to be reached by the driver, or the last address if all legs are completed.
  const activeLeg = getActiveLeg(shipment);
  return shouldBeCollected(activeLeg)
    ? `${activeLeg.origin_address_name ? activeLeg.origin_address_name + ' - ' : ''}${activeLeg.origin_address}`
    : `${activeLeg.destination_address_name ? activeLeg.destination_address_name + ' - ' : ''}${activeLeg.destination_address}`;
};

type Position = [number, number];

// Source: https://developers.google.com/maps/documentation/urls/get-started
export const buildDirectionsLink = (position: Position | null, address: string) => {
  const baseUrl = 'https://www.google.com/maps/search/?api=1';
  const query = position ? [...position].reverse().join(',') : address;
  const encodedQuery = encodeURIComponent(query);
  const fullUrl = `${baseUrl}&query=${encodedQuery}`;
  return fullUrl;
};

export const isNetworkError = (error?: unknown) =>
  Boolean(error) && typeof error === 'object' && ((error as { code: string }).code === 'ERR_NETWORK' || (error as { message: string }).message === 'Failed to fetch');

// this helper is useful to handle timeouts in async functions, and you can either provide a default value to resolve on timeout or the error to reject with
export const withTimeout = (fn: any, timeout: number, defaultValueOrError: any) =>
  Promise.race([
    fn,
    new Promise((resolve, reject) =>
      setTimeout(() => {
        if (defaultValueOrError instanceof Error) reject(defaultValueOrError);
        else resolve(defaultValueOrError);
      }, timeout),
    ),
  ]);

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replaceAll('-', '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const encrypt = async (document: ArrayBuffer): Promise<{ encryptionKey: string; encryptedDocument: Buffer }> => {
  const encryptionKey = await crypto.subtle.generateKey({ name: 'AES-CBC', length: 256 }, true, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const encryptedDocument = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, encryptionKey, document);
  const encryptionKeyAsString = Buffer.from(new Uint8Array([...new Uint8Array(await crypto.subtle.exportKey('raw', encryptionKey)), ...iv])).toString('base64');
  return { encryptionKey: encryptionKeyAsString, encryptedDocument: Buffer.from(encryptedDocument) };
};

export const convertStringToArrayBuffer = (str: string) => {
  const textEncoder = new TextEncoder();
  return textEncoder.encode(str).buffer;
};

export const convertBufferToArrayBuffer = (buffer: Buffer) => {
  const arrayBuffer = new ArrayBuffer(buffer.length);
  const view = new Uint8Array(arrayBuffer);
  for (let i = 0; i < buffer.length; ++i) {
    view[i] = buffer[i];
  }
  return arrayBuffer;
};

export const bytesToBase64 = (buffer: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buffer)));

export const base64ToBytes = (base64: string) => Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)).buffer;

const hexToUint8Array = (hex: string) => {
  if (hex.startsWith('0x')) hex = hex.slice(2);
  const matches = hex.match(/.{1,2}/g);
  if (!matches) throw new Error('Invalid hex string');
  return new Uint8Array(matches.map((byte) => Number.parseInt(byte, 16)));
};

export const uint8ArrayToHex = (bytes: Uint8Array) => {
  let convertedBack = '';
  for (const b of bytes) {
    if (b < 16) convertedBack += '0';
    convertedBack += b.toString(16);
  }
  return convertedBack;
};

export const toArrayBuffer = (uint8Array: Uint8Array): ArrayBuffer => uint8Array.slice().buffer;

export const encryptDocument = async (document: ArrayBuffer): Promise<{ encryptionKey: string; encryptedDocument: ArrayBuffer }> => {
  const encryptionKey = await crypto.subtle.generateKey({ name: 'AES-CBC', length: 256 }, true, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const encryptedDocument = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, encryptionKey, document);
  const encryptionKeyAsString = bytesToBase64(toArrayBuffer(new Uint8Array([...iv, ...new Uint8Array(await crypto.subtle.exportKey('raw', encryptionKey))])));
  return { encryptionKey: encryptionKeyAsString, encryptedDocument };
};

export const decryptDocument = async (encryptionKeyBase64: string, encryptedDocument: ArrayBuffer) => {
  const fullEncryptionKey = base64ToBytes(encryptionKeyBase64);
  const iv = fullEncryptionKey.slice(0, 16);
  const encryptionKeyBytes = fullEncryptionKey.slice(16);
  const encryptionKey = await crypto.subtle.importKey('raw', encryptionKeyBytes, 'AES-CBC', true, ['encrypt', 'decrypt']);
  const decryptedDocument = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, encryptionKey, encryptedDocument);
  return decryptedDocument;
};

export const getFilenameFromUrl = (url: string, includeUserID = true): string | null => {
  /**
   * URLs can either be:
   * - https://.../vault/<userID>/<hash> (when the doc is in the user workspace, before moving to root)
   * - https://.../vault/<hash> (doc is in root)
   * It returns the hash part of the URL, or the userID/hash if includeUserID is true.
   */
  try {
    const { pathname } = new URL(url);
    let filename = includeUserID ? pathname.slice(1).split('/').slice(1).join('/') : pathname.split('/').pop() || null;
    if (filename) filename = decodeURIComponent(filename);
    return filename;
  } catch {
    return null;
  }
};

export const computeHash = async (document: ArrayBuffer): Promise<string> => {
  const buffer = new Uint8Array(document);
  const hashArrayBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hash = uint8ArrayToHex(new Uint8Array(hashArrayBuffer));
  return hash;
};

export const addPrefix = (str: string, prefix: string) => (str.startsWith(prefix) ? str : `${prefix}${str}`);

export const addHexPrefix = (hex: string) => addPrefix(hex, '0x');

export const zeroPadBytes = (bytes: Uint8Array, length: number) => {
  if (length < bytes.length) throw new BaseError(errorCodes.PADDING_EXCEEDS_DATA_LENGTH, errorMessages.PADDING_EXCEEDS_DATA_LENGTH);
  const result = new Uint8Array(length);
  result.fill(0);
  result.set(bytes, 0);
  return result;
};

export const addressToBeSigned = (address: string) => {
  const addressAsBytes = hexToUint8Array(address);
  const paddedAddress = zeroPadBytes(addressAsBytes, 32);
  return paddedAddress;
};

export const hashStringToUint8Array = async (string: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(string);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash);
};

export const bytesToHex = (arrayBuffer: ArrayBuffer | Uint8Array) => {
  if (typeof arrayBuffer !== 'object' || arrayBuffer === null || typeof arrayBuffer.byteLength !== 'number') {
    throw new TypeError('Expected input to be an ArrayBuffer');
  }

  const view = new Uint8Array(arrayBuffer);
  let result = '';

  for (const value of view) {
    result += value.toString(16).padStart(2, '0');
  }

  return result;
};

export const hashStringToHex = async (string: string) => {
  const hash = await hashStringToUint8Array(string);
  return bytesToHex(hash);
};

export const parseDocumentInfo = (document: DocumentBC) => {
  const filename = document.metadata.filename ?? 'UNNAMED';
  const type = document.metadata.type ?? 'generic';
  const info = { filename, url: document.url, encryptionKey: document.encryptionKey, metadata: document.metadata, type, originalUrl: document.url };
  return info;
};

export const mapToDocumentBC = (document_struct: Vault.DocumentStructOutput, hash: string): DocumentBC => {
  const metadata = document_struct.metadata.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}); // we do this before toObject because it's providing a weird metadata object {_:{}}
  // @ts-expect-error @bruno why toObject does not exist?
  const document = document_struct.toObject(true);
  const result: DocumentBC = { ...document, timestamp: new Date(Number(document.timestamp)).toISOString(), hash, metadata };
  return result;
};

export const shouldBeCollected = (leg?: Leg | null) => leg?.status === 'pending';
export const shouldBeDelivered = (leg?: Leg | null) => leg?.status === 'transiting';

export const typeGuard = <T>(value: T | undefined): value is T => value !== undefined;

export const removeEmptyLines = (strings: TemplateStringsArray, ...values: any[]) => {
  const fullstring = strings.reduce((result, string, i) => result + string + (values[i] || ''), '');
  const result = fullstring
    .split('\n')
    .filter((line) => line.trim() !== '')
    .join('\n');
  return result;
};

// we only allow win32 valid filenames to be used so the user can download and have the files named correctly
export const sanitizeFilename = (filename: string): string => {
  const INVALID_CHARACTERS_REGEX = /[^a-zA-Z0-9_.\- ]/g;
  const MAX_FILENAME_LENGTH = 50;

  // separate name and extension
  const extensionIndex = filename.lastIndexOf('.');
  const hasExtension = extensionIndex > 0 && extensionIndex < filename.length - 1;
  const name = hasExtension ? filename.slice(0, extensionIndex) : filename;
  const extension = hasExtension ? filename.slice(extensionIndex) : '';

  // remove invalid characters
  let sanitized = name.replace(INVALID_CHARACTERS_REGEX, '_');
  // replace sequence of spaces and underscores
  sanitized = sanitized.replace(/\s+/g, ' ').replace(/_+/g, '_');
  // trim to max length
  if (sanitized.length > MAX_FILENAME_LENGTH) sanitized = sanitized.slice(0, MAX_FILENAME_LENGTH);
  // add extension back
  if (extension) sanitized += extension;

  return sanitized;
};

export const statusToFill = (status: LEG_STATUS) => {
  const statusToFillMap: Partial<Record<LEG_STATUS, string>> = {
    pending: 'fill-grey-light',
    transiting: 'fill-grey-light',
    delivered: 'fill-green',
  };
  const result = statusToFillMap[status] || 'fill-grey-light';
  return result;
};

export const getRuntimeTimezone = (truncate = false) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return truncate ? truncateTimezone(timezone) : timezone;
};

export const truncateTimezone = (timezone: string) => {
  const [, city] = timezone.split('/');
  return city;
};

export const truncateToDecimals = (value: number, decimals = 5) => {
  const factor = 10 ** decimals;
  const result = Math.trunc(value * factor) / factor;
  return result;
};

export const isGranted = (user: any, operation: OPERATION, resource_id: string): boolean => {
  const subject$in = [user.id, user.owner_id]; // either the user (that could be a group as well) or the user's group
  const operation$in = [operation, '*']; // either the operation or all operations

  const [resource, resource_is_owner] = resource_id.split('/');
  const resource$in = [resource, '*']; // either the resource or all resources
  const resource_is_owner$eq = !!resource_is_owner;

  for (const permission of user.permissions) {
    const subject_ok = subject$in.includes(permission.subject);
    const operation_ok = operation$in.includes(permission.operation);
    const resource_specific_ok = resource$in.includes(permission.resource);
    const resource_ownership_ok = resource$in.includes(permission.resource) && permission.resource_is_owner === resource_is_owner$eq;
    const granted = subject_ok && operation_ok && (resource_specific_ok || resource_ownership_ok);
    if (granted) return true;
  }
  return false;
};

// is_support_admin is set while the user holds an active ADMINISTRATOR support grant, temporarily conferring super-admin status
export const isSuperadmin = (user: any): boolean => user.group_role === 'SUPERADMIN' || user.is_support_admin === true;

export const getCountryCode = (country: string): string | undefined => {
  const countryCodes = {
    afghanistan: 'AF',
    'aland islands': 'AX',
    albania: 'AL',
    algeria: 'DZ',
    americansamoa: 'AS',
    andorra: 'AD',
    angola: 'AO',
    anguilla: 'AI',
    antarctica: 'AQ',
    'antigua and barbuda': 'AG',
    argentina: 'AR',
    armenia: 'AM',
    aruba: 'AW',
    australia: 'AU',
    austria: 'AT',
    azerbaijan: 'AZ',
    bahamas: 'BS',
    bahrain: 'BH',
    bangladesh: 'BD',
    barbados: 'BB',
    belarus: 'BY',
    belgium: 'BE',
    belize: 'BZ',
    benin: 'BJ',
    bermuda: 'BM',
    bhutan: 'BT',
    'bolivia, plurinational state of': 'BO',
    'bosnia and herzegovina': 'BA',
    botswana: 'BW',
    brazil: 'BR',
    'british indian ocean territory': 'IO',
    'brunei darussalam': 'BN',
    bulgaria: 'BG',
    'burkina faso': 'BF',
    burundi: 'BI',
    cambodia: 'KH',
    cameroon: 'CM',
    canada: 'CA',
    'cape verde': 'CV',
    'cayman islands': 'KY',
    'central african republic': 'CF',
    chad: 'TD',
    chile: 'CL',
    china: 'CN',
    'christmas island': 'CX',
    'cocos (keeling) islands': 'CC',
    colombia: 'CO',
    comoros: 'KM',
    congo: 'CG',
    'congo, the democratic republic of the congo': 'CD',
    'cook islands': 'CK',
    'costa rica': 'CR',
    "cote d'ivoire": 'CI',
    croatia: 'HR',
    cuba: 'CU',
    cyprus: 'CY',
    'czech republic': 'CZ',
    denmark: 'DK',
    djibouti: 'DJ',
    dominica: 'DM',
    'dominican republic': 'DO',
    ecuador: 'EC',
    egypt: 'EG',
    'el salvador': 'SV',
    'equatorial guinea': 'GQ',
    eritrea: 'ER',
    estonia: 'EE',
    ethiopia: 'ET',
    'falkland islands (malvinas)': 'FK',
    'faroe islands': 'FO',
    fiji: 'FJ',
    finland: 'FI',
    france: 'FR',
    'french guiana': 'GF',
    'french polynesia': 'PF',
    gabon: 'GA',
    gambia: 'GM',
    georgia: 'GE',
    germany: 'DE',
    ghana: 'GH',
    gibraltar: 'GI',
    greece: 'GR',
    greenland: 'GL',
    grenada: 'GD',
    guadeloupe: 'GP',
    guam: 'GU',
    guatemala: 'GT',
    guernsey: 'GG',
    guinea: 'GN',
    'guinea-bissau': 'GW',
    guyana: 'GY',
    haiti: 'HT',
    'holy see (vatican city state)': 'VA',
    honduras: 'HN',
    'hong kong': 'HK',
    hungary: 'HU',
    iceland: 'IS',
    india: 'IN',
    indonesia: 'ID',
    'iran, islamic republic of persian gulf': 'IR',
    iraq: 'IQ',
    ireland: 'IE',
    'isle of man': 'IM',
    israel: 'IL',
    italy: 'IT',
    jamaica: 'JM',
    japan: 'JP',
    jersey: 'JE',
    jordan: 'JO',
    kazakhstan: 'KZ',
    kenya: 'KE',
    kiribati: 'KI',
    "korea, democratic people's republic of korea": 'KP',
    'korea, republic of south korea': 'KR',
    kuwait: 'KW',
    kyrgyzstan: 'KG',
    laos: 'LA',
    latvia: 'LV',
    lebanon: 'LB',
    lesotho: 'LS',
    liberia: 'LR',
    'libyan arab jamahiriya': 'LY',
    liechtenstein: 'LI',
    lithuania: 'LT',
    luxembourg: 'LU',
    macao: 'MO',
    macedonia: 'MK',
    madagascar: 'MG',
    malawi: 'MW',
    malaysia: 'MY',
    maldives: 'MV',
    mali: 'ML',
    malta: 'MT',
    'marshall islands': 'MH',
    martinique: 'MQ',
    mauritania: 'MR',
    mauritius: 'MU',
    mayotte: 'YT',
    mexico: 'MX',
    'micronesia, federated states of micronesia': 'FM',
    moldova: 'MD',
    monaco: 'MC',
    mongolia: 'MN',
    montenegro: 'ME',
    montserrat: 'MS',
    morocco: 'MA',
    mozambique: 'MZ',
    myanmar: 'MM',
    namibia: 'NA',
    nauru: 'NR',
    nepal: 'NP',
    netherlands: 'NL',
    'netherlands antilles': 'AN',
    'new caledonia': 'NC',
    'new zealand': 'NZ',
    nicaragua: 'NI',
    niger: 'NE',
    nigeria: 'NG',
    niue: 'NU',
    'norfolk island': 'NF',
    'northern mariana islands': 'MP',
    norway: 'NO',
    oman: 'OM',
    pakistan: 'PK',
    palau: 'PW',
    'palestinian territory, occupied': 'PS',
    panama: 'PA',
    'papua new guinea': 'PG',
    paraguay: 'PY',
    peru: 'PE',
    philippines: 'PH',
    pitcairn: 'PN',
    poland: 'PL',
    portugal: 'PT',
    'puerto rico': 'PR',
    qatar: 'QA',
    romania: 'RO',
    russia: 'RU',
    rwanda: 'RW',
    reunion: 'RE',
    'saint barthelemy': 'BL',
    'saint helena, ascension and tristan da cunha': 'SH',
    'saint kitts and nevis': 'KN',
    'saint lucia': 'LC',
    'saint martin': 'MF',
    'saint pierre and miquelon': 'PM',
    'saint vincent and the grenadines': 'VC',
    samoa: 'WS',
    'san marino': 'SM',
    'sao tome and principe': 'ST',
    'saudi arabia': 'SA',
    senegal: 'SN',
    serbia: 'RS',
    seychelles: 'SC',
    'sierra leone': 'SL',
    singapore: 'SG',
    slovakia: 'SK',
    slovenia: 'SI',
    'solomon islands': 'SB',
    somalia: 'SO',
    'south africa': 'ZA',
    'south sudan': 'SS',
    'south georgia and the south sandwich islands': 'GS',
    spain: 'ES',
    'sri lanka': 'LK',
    sudan: 'SD',
    suriname: 'SR',
    'svalbard and jan mayen': 'SJ',
    swaziland: 'SZ',
    sweden: 'SE',
    switzerland: 'CH',
    'syrian arab republic': 'SY',
    taiwan: 'TW',
    tajikistan: 'TJ',
    'tanzania, united republic of tanzania': 'TZ',
    thailand: 'TH',
    'timor-leste': 'TL',
    togo: 'TG',
    tokelau: 'TK',
    tonga: 'TO',
    'trinidad and tobago': 'TT',
    tunisia: 'TN',
    turkey: 'TR',
    turkmenistan: 'TM',
    'turks and caicos islands': 'TC',
    tuvalu: 'TV',
    uganda: 'UG',
    ukraine: 'UA',
    'united arab emirates': 'AE',
    'united kingdom': 'GB',
    'united states': 'US',
    uruguay: 'UY',
    uzbekistan: 'UZ',
    vanuatu: 'VU',
    'venezuela, bolivarian republic of venezuela': 'VE',
    vietnam: 'VN',
    'virgin islands, british': 'VG',
    'virgin islands, u.s.': 'VI',
    'wallis and futuna': 'WF',
    yemen: 'YE',
    zambia: 'ZM',
    zimbabwe: 'ZW',
  };
  return countryCodes[country.toLowerCase()] || country;
};

interface ShortAddressComponents {
  postcode?: string | null;
  city?: string | null;
  country?: string | null;
}

// Returns an array of non-empty lines for a short address cell:
//   [postcode, "city - country_code"]  (omits missing parts)
export function formatShortAddress(components: ShortAddressComponents): string[] {
  const countryCode = components.country ? getCountryCode(components.country) : undefined;
  const cityCountry = [components.city, countryCode].filter(Boolean).join(' - ');
  return [components.postcode, cityCountry].filter(Boolean) as string[];
}

export const stringifyPermission = (values: PermissionParsed) => {
  const { subject, operation, resource } = values;
  const result = `Sub=${subject}; Op=${operation}; Res=${resource}`;
  return result;
};

export const chunkMap = async <T, R>(items: T[], chunkSize: number, fn: (item: T, index: number) => Promise<R>, sleepTime?: number): Promise<R[]> => {
  const chunks = chunk(items, chunkSize);
  const results: R[] = [];
  for (const chunkItems of chunks) {
    const chunkResults = await Promise.all(chunkItems.map(fn));
    results.push(...chunkResults);
    if (sleepTime) await sleep(sleepTime);
  }
  return results;
};
